import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Breadcrumbs, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify, useRedirect } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  ConditionWithRelations,
  ConditionWithRelationsForm,
  UpdateConditionPayload,
} from '../../../../interfaces/health-data';
import healthDataService from '../../../../services/health-data';
import { LoaderBubble } from '../../../common/LoaderBubble/LoaderBubble';
import { ConditionForm } from './ConditionForm/ConditionForm';
import './css/conditions.css';

export const EditCondition = () => {
  const [form, setForm] = useState<UpdateConditionPayload | null>(null);
  const [conditionWithRelations, setConditionWithRelations] =
    useState<ConditionWithRelations | null>(null);
  const [breadcrumbLabels, setBreadcrumbLabels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { conditionId } = useParams<{ conditionId: string }>();
  const redirect = useRedirect();

  const notify = useNotify();

  const handleSubmit = async () => {
    if (!form || !conditionWithRelations?.condition?.id) {
      notify('There is an unknown error updating the condition', 'warning');
      return;
    }

    setIsLoading(true);

    const response = await healthDataService.updateCondition(
      conditionWithRelations.condition.id,
      form,
    );

    if (!response.data) {
      notify(response.message || 'There was an error updating the condition', 'warning');
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    notify('Condition updated successfully');
    redirect(`/medical-setup/conditions/${response.data.id}/show`);
  };

  const handleUpdateForm = (form: ConditionWithRelationsForm) => {
    const { condition, laboratoryTests, procedures, medications } = form;

    const laboratoryTestParams = laboratoryTests.map((lb) => ({
      id: lb.laboratoryTest.id,
      validityPeriodFrom: lb.validityPeriodFrom,
      validityPeriodUntil: lb.validityPeriodUntil,
    }));

    const proceduresParams = procedures.map((p) => ({
      id: p.procedure.id,
      validityPeriodFrom: p.validityPeriodFrom,
      validityPeriodUntil: p.validityPeriodUntil,
    }));

    const medicationsParams = medications.map((md) => ({
      id: md.medication.id,
    }));

    setForm({
      ...{
        name: condition.name,
        description: condition.description || '',
        status: condition.status,
        billingCriteria: condition.billingCriteria,
        icd10Code: condition.icd10Code,
        includedInPvq: `${condition.includedInPvq}` === 'true',
        includedInNotes: `${condition.includedInNotes}` === 'true',
        includeInMedicalHistorySummary: `${condition.includeInMedicalHistorySummary}` === 'true',
      },
      ...(condition.nameInPvq && { nameInPvq: condition.nameInPvq }),
      ...(condition.bmiCriteria && { bmiCriteria: condition.bmiCriteria }),
      ...(condition.billingCriteria && { billingCriteria: condition.billingCriteria }),
      ...(laboratoryTestParams.length > 0 && { laboratoryTests: laboratoryTestParams }),
      ...(proceduresParams.length > 0 && { procedures: proceduresParams }),
      ...(medicationsParams.length > 0 && { medications: medicationsParams }),
    });
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const response = await healthDataService.getCondition(Number(conditionId));

      if (!response.data) {
        notify(response.message || 'There was an error obtaining the condition', 'warning');
        setIsLoading(false);
        return;
      }

      setBreadcrumbLabels(['conditions', response.data.condition.name || 'Unknown']);
      setConditionWithRelations(response.data);
      setIsLoading(false);
    })();
  }, [conditionId]);

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' className='tab-section-action-bar'>
        <Breadcrumbs separator={<NavigateNextIcon fontSize='small' />}>
          {breadcrumbLabels.map((text, index) => {
            return (
              <Typography key={`text-condition-breadcrumb-${index}`} textTransform='capitalize'>
                {text}
              </Typography>
            );
          })}
        </Breadcrumbs>

        <Button
          onClick={handleSubmit}
          disabled={!form}
          variant='contained'
          sx={{ textTransform: 'capitalize' }}>
          Save
        </Button>
      </Box>

      {conditionWithRelations && !isLoading ? (
        <ConditionForm
          onChangeForm={(v) => handleUpdateForm(v)}
          conditionWithRelations={conditionWithRelations}
        />
      ) : (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          height='100%'
          width='100%'
          flexDirection='column'>
          <Typography mb={3}>Wait a few seconds...</Typography>
          <LoaderBubble />
        </Box>
      )}
    </Box>
  );
};
